import { LoaderFunctionArgs, replace } from 'react-router-dom'

import { parseRouteInitData } from '../../util-routing'
import * as api from './services/api'
import {
  OptionsByDateLoader,
  OptionsByStrikeLoader,
  OptionsLoaderData,
  OptionsQuery,
  OptionsView,
  PlotLoader,
} from './types'
import { OptionsParsedQuery, parseOptionsQuery } from './utils/query'

export async function loader(args: LoaderFunctionArgs) {
  const url = new URL(args.request.url)
  const parsedQuery = parseOptionsQuery(url.searchParams)

  /**
   * Add expiry query param on initial response
   */
  const isDateView = [OptionsView.ChainByDate, OptionsView.ListByDate].includes(parsedQuery.view)
  const expiry = parsedQuery.expiry
  // Do not remove the init element if we’re missing expiry, the loader will run again after redirect
  const initialResponse = parseRouteInitData<OptionsLoaderData>({ removeElement: isDateView ? !!expiry : true })
  if (initialResponse) {
    const dateLoaderInit = initialResponse as OptionsByDateLoader
    if (isDateView && (!expiry || !dateLoaderInit.expiries.includes(expiry!))) {
      url.searchParams.set(OptionsQuery.Expiry, dateLoaderInit.currentExpiry)
      return replace(url.toString())
    }
    return initialResponse
  }

  switch (parsedQuery.view) {
    case OptionsView.Plot:
      return plotDataLoader(parsedQuery)
    case OptionsView.ChainByStrike:
    case OptionsView.ListByStrike:
      return optionsByStrikeLoader(parsedQuery)
    case OptionsView.ChainByDate:
    case OptionsView.ListByDate:
    default:
      return optionsByDateLoader(parsedQuery)
  }
}

async function plotDataLoader(query: OptionsParsedQuery): Promise<PlotLoader | null> {
  const optionsData = await api.optionsData(query.ticker, { filterLastClose: true })

  if (!optionsData) return null

  return {
    view: OptionsView.Plot,
    subpage: query.type,
    ...optionsData,
  }
}

export async function optionsByStrikeLoader(query: OptionsParsedQuery): Promise<OptionsByStrikeLoader | null> {
  try {
    const strikes = await api.strikesData(query.ticker)
    if (!strikes) return null

    const strike = query.strike && strikes.includes(query.strike) ? query.strike : strikes[0]
    const optionsData = await api.optionsData(query.ticker, { strike })

    if (!optionsData) return null

    return {
      // This is already guarded by a switch
      view: query.view as OptionsView.ChainByStrike | OptionsView.ListByStrike,
      subpage: query.type,
      ticker: query.ticker,
      strikes: strikes,
      currentStrike: strike,
      ...optionsData,
    }
  } catch {}

  return null
}

export async function optionsByDateLoader(query: OptionsParsedQuery): Promise<OptionsByDateLoader | null> {
  try {
    const expiries = await api.expiriesData(query.ticker)
    if (!expiries) return null

    const expiry = query.expiry && expiries.includes(query.expiry) ? query.expiry : expiries[0]
    const optionsData = await api.optionsData(query.ticker, { expiry })

    if (!optionsData) return null

    return {
      // This is already guarded by a switch
      view: query.view as OptionsView.ChainByDate | OptionsView.ListByDate,
      subpage: query.type,
      ticker: query.ticker,
      expiries,
      currentExpiry: expiry,
      ...optionsData,
    }
  } catch {}

  return null
}
